/**
 * PWA entry-point intake (wave 7,
 * pwa-versioning:manifest-entry-points-unconsumed): the two manifest entry
 * points that existed only as declarations are consumed here.
 *
 * 1. file_handlers → `window.launchQueue`. `initLaunchQueue()` (called once
 *    from main.tsx) registers the consumer; launched file handles are read
 *    into Files and buffered until the app mounts and registers its handler
 *    via `setLaunchFilesConsumer` (AppNext feeds them into loadFiles, the
 *    same path a drop takes).
 *
 * 2. share_target → the service worker stores one POSTed share payload under
 *    a reserved cache key and redirects to "/?shared=1" (public/sw.js).
 *    `readSharedTarget()` consumes it on boot: strips the marker from the
 *    URL (synchronously, so a double-invoked effect cannot consume twice),
 *    finds the payload in any of our caches, deletes it, and returns it as a
 *    File. Everything stays on this device.
 */

import { CACHE_STORAGE } from "../utils/storageKeys";

type LaunchFilesConsumer = (files: File[]) => void;

let consumer: LaunchFilesConsumer | null = null;
const pendingBatches: File[][] = [];

/** Hand a batch of launched files to the app, or buffer until it mounts. */
export function deliverLaunchFiles(files: File[]): void {
  if (files.length === 0) return;
  if (consumer) consumer(files);
  else pendingBatches.push(files);
}

/** Register (or clear) the app-side handler; buffered batches flush to it. */
export function setLaunchFilesConsumer(fn: LaunchFilesConsumer | null): void {
  consumer = fn;
  if (fn) for (const batch of pendingBatches.splice(0, pendingBatches.length)) fn(batch);
}

interface LaunchParamsLike {
  files?: readonly { getFile(): Promise<File> }[];
}
interface LaunchQueueLike {
  setConsumer(cb: (params: LaunchParamsLike) => void): void;
}

/** Wire the OS file-open entry point (manifest file_handlers). */
export function initLaunchQueue(): void {
  const lq = (globalThis as { launchQueue?: LaunchQueueLike }).launchQueue;
  if (!lq || typeof lq.setConsumer !== "function") return;
  lq.setConsumer((params) => {
    void (async () => {
      const files: File[] = [];
      for (const handle of params?.files ?? []) {
        try {
          files.push(await handle.getFile());
        } catch {
          /* an unreadable handle is skipped; the rest still load */
        }
      }
      deliverLaunchFiles(files);
    })();
  });
}

/**
 * The reserved cache key the service worker stores a share payload under.
 *
 * Read from the registry rather than retyped: `public/sw.js` writes under this
 * name and cannot import, so the two halves used to be independent literals
 * and a one-character drift here was silent (the intake below would find
 * nothing and Settings could no longer erase a parked payload). The SW's own
 * literal is held to the same registry entry by
 * `scripts/audit-storage-keys.mjs`.
 */
export const SHARE_CACHE_KEY: string = CACHE_STORAGE.shareTarget;

/**
 * Consume a pending Web Share Target payload, if this boot came from one.
 * Returns the shared payload as a File (named via the X-Share-Name header
 * the SW stored), or null when there is nothing to consume.
 */
export async function readSharedTarget(): Promise<File | null> {
  try {
    if (typeof location === "undefined" || !("caches" in globalThis)) return null;
    if (!new URLSearchParams(location.search).has("shared")) return null;
    // Strip the marker first (and synchronously) so a reload, and the second
    // run of a StrictMode double-effect, cannot re-consume.
    history.replaceState(history.state, "", location.pathname + location.hash);
    for (const key of await caches.keys()) {
      const cache = await caches.open(key);
      const res = await cache.match(SHARE_CACHE_KEY);
      if (!res) continue;
      await cache.delete(SHARE_CACHE_KEY);
      const name = decodeURIComponent(res.headers.get("X-Share-Name") ?? "shared.eml");
      const blob = await res.blob();
      return new File([blob], name, { type: blob.type || "message/rfc822" });
    }
    return null;
  } catch {
    return null;
  }
}
